/**
 * The account page's controller (part 36c, tables PF, CH, FX, RQ, PD, DV, NO, TA, XP, DA): the account's lists (its
 * devices, its open changes, its second factors, its notices), read with the session (`Account.get`), and every
 * action the writer takes on the account, each after its fresh proof (C9), its outcome the relay's word (C10). After an
 * action the lists and the account's view are read again. Nothing here reads or writes a book (C14). The interface
 * holds no secret longer than its field: a grant goes straight from the proof to its one action.
 */
import { createSignal } from 'solid-js';
import { KDF_DEFAULT } from '@cockatrice/crypto';
import type { KdfParameters } from '@cockatrice/crypto';
import type { Account } from './account.ts';
import {
  addFactor,
  askDeletion,
  cancelChange,
  cancelDeletion,
  cancelDeletionByLink,
  changeEmail,
  changePassword,
  claimReset,
  confirmAdd,
  confirmEnrolment,
  confirmLink,
  newBackupCodes,
  proveSecondStep,
  removeFactor,
  requestReset,
  revokeDevice,
  setUpEnrolment,
  startEnrolment,
  startTypedProof,
} from './actions.ts';
import type { FactorKind, NamedFactor, NewFactorProof, Outcome } from './actions.ts';
import { canonicalEmail } from './email.ts';
import type { Email } from './email.ts';
import type { Problem, SecondProof, Typed, Versions } from './flows.ts';
import type { AcceptOutcome } from '../shared/shares.ts';
import { problemOf } from './flows.ts';
import { readChanges, readDevices, readExport, readFactors, readNotices, readTermsAccepted } from './wire.ts';
import type { AccountId, Advanced, ChangeEntry, DeviceEntry, FactorSetup, Factors, Granted, Notice, SecondFactorNeeded, TermsStatus } from './wire.ts';

/** What the page holds of a list: not read yet, read, or its read failed (the page says so and offers again). */
export type Listed<T> = { readonly kind: 'loading' } | { readonly kind: 'ok'; readonly value: T } | { readonly kind: 'failed' };

/** PD1, PD5: how often the lists may be read again on their own (a page shown again, another tab's change). */
export const LIST_PAUSE_MS = 60_000;

const FAILED: Problem = Object.freeze({ kind: 'failed' });

/**
 * Part 38c (KG9): the guest keyring's side of a change of P: its wrap under the new password's `next`, from the keyring
 * the device holds, or the relay's opened with `previous` (what the proof's password gave); see `Rewrap`.
 */
export interface KeyringAccess {
  rewrap(account: AccountId, next: Typed, previous: Typed | null): Promise<{ readonly id: string; readonly wrapped: string } | null | 'failed'>;
  /** Part 38c, JL3: the waiting invite claimed with `grant`, the proof's password having given `typed`. */
  accept(grant: string, typed: Typed): Promise<AcceptOutcome>;
  /** Part 38c, AS8 (KG11): the account's keyring opened with what the proof's password gave. */
  openKeyring(typed: Typed): Promise<'opened' | 'none' | 'refused' | 'failed'>;
}

/** The account manager. */
export class AccountManager {
  private readonly account: Account;
  private readonly now: () => number;
  private readonly devicesSignal = createSignal<Listed<readonly DeviceEntry[]>>({ kind: 'loading' });
  private readonly changesSignal = createSignal<Listed<readonly ChangeEntry[]>>({ kind: 'loading' });
  private readonly factorsSignal = createSignal<Listed<Factors>>({ kind: 'loading' });
  private readonly noticesSignal = createSignal<Listed<readonly Notice[]>>({ kind: 'loading' });
  /** NO2: the newest `new-device` notice from another device not seen yet on this device; null for none. */
  private readonly unseenSignal = createSignal<Notice | null>(null);
  /** Part 36c, TA: the terms' status after an acceptance on this page (the view's until then). */
  private readonly termsSignal = createSignal<TermsStatus | null>(null);
  private lastLoad: number | null = null;
  private loadedFor: AccountId | null = null;
  private readonly keyring: KeyringAccess | null;
  /** KG9: what the last proof's password gave, for a change of P's rewrap; zeroed when replaced or spent. */
  private provenFor: { readonly account: AccountId; readonly typed: Typed } | null = null;

  constructor(account: Account, now: () => number, keyring: KeyringAccess | null = null) {
    this.account = account;
    this.now = now;
    this.keyring = keyring;
  }

  /** DV1: the account's devices. */
  readonly devices = (): Listed<readonly DeviceEntry[]> => this.devicesSignal[0]();

  /** PD1: the account's open changes. */
  readonly changes = (): Listed<readonly ChangeEntry[]> => this.changesSignal[0]();

  /** FX5: the account's second factors. */
  readonly factors = (): Listed<Factors> => this.factorsSignal[0]();

  /** NO1: the account's newest notices. */
  readonly notices = (): Listed<readonly Notice[]> => this.noticesSignal[0]();

  /** NO2: the newest new-device notice from another device, not seen here; null for none. */
  readonly unseen = (): Notice | null => this.unseenSignal[0]();

  /** TA1: the terms' status: the newest this page knows (an acceptance's, else the account's view's); null before. */
  readonly terms = (): TermsStatus | null => {
    const view = this.account.view();
    const accepted = this.termsSignal[0]();
    return accepted !== null && view !== null && this.loadedFor === view.account ? accepted : (view?.terms ?? null);
  };

  /** The account logged in with a session to act with, its ID and address; null otherwise. */
  private who(): { readonly id: AccountId; readonly email: Email } | null {
    const status = this.account.status();
    return status.kind === 'account' && !status.signedOut ? { id: status.id, email: status.email } : null;
  }

  /**
   * PD1, DV1, FX5, NO1: every list read again, and the account's view; `force` reads at once, otherwise at most once a
   * {@link LIST_PAUSE_MS}. Logged out (or signed out) the lists empty.
   */
  async load(force = false): Promise<void> {
    const who = this.who();
    if (who === null) {
      this.loadedFor = null;
      this.lastLoad = null;
      this.clear();
      return;
    }
    const now = this.now();
    if (!force && this.loadedFor === who.id && this.lastLoad !== null && now - this.lastLoad < LIST_PAUSE_MS) return;
    if (this.loadedFor !== who.id) {
      this.clear();
      this.termsSignal[1](null);
    }
    this.loadedFor = who.id;
    this.lastLoad = now;
    await this.account.refreshView();
    const [devices, changes, factors, notices] = await Promise.all([
      this.account.get('/v1/devices', readDevices),
      this.account.get('/v1/account/changes', readChanges),
      this.account.get('/v1/account/second-factor', readFactors),
      this.account.get('/v1/notices', readNotices),
    ]);
    // Another account logged in meanwhile: these lists aren't its.
    if (this.who()?.id !== who.id) return;
    this.devicesSignal[1](devices.kind === 'ok' ? { kind: 'ok', value: devices.value } : { kind: 'failed' });
    this.changesSignal[1](changes.kind === 'ok' ? { kind: 'ok', value: changes.value } : { kind: 'failed' });
    this.factorsSignal[1](factors.kind === 'ok' ? { kind: 'ok', value: factors.value } : { kind: 'failed' });
    this.noticesSignal[1](notices.kind === 'ok' ? { kind: 'ok', value: notices.value } : { kind: 'failed' });
    if (notices.kind === 'ok') await this.findUnseen(notices.value, devices.kind === 'ok' ? devices.value : []);
  }

  private clear(): void {
    this.devicesSignal[1]({ kind: 'loading' });
    this.changesSignal[1]({ kind: 'loading' });
    this.factorsSignal[1]({ kind: 'loading' });
    this.noticesSignal[1]({ kind: 'loading' });
    this.unseenSignal[1](null);
  }

  /** NO2: the newest new-device notice from another device beyond what this device has seen; the first look marks all seen. */
  private async findUnseen(notices: readonly Notice[], devices: readonly DeviceEntry[]): Promise<void> {
    const seen = await this.account.seenNotice();
    const newest = notices.reduce((most, notice) => Math.max(most, notice.id), 0);
    if (seen === null) {
      // NO2: a device's first look: what came before it is history (the list shows it), not news.
      this.unseenSignal[1](null);
      await this.account.seeNotice(newest);
      return;
    }
    const self = devices.find((device) => device.current)?.id;
    const fresh = notices.filter((notice) => notice.kind === 'new-device' && notice.id > seen && notice.device !== self);
    this.unseenSignal[1](fresh.reduce<Notice | null>((most, notice) => (most === null || notice.id > most.id ? notice : most), null));
  }

  /** NO2: the writer has seen the new-device notices up to the newest. */
  async seen(): Promise<void> {
    const notices = this.notices();
    const newest = notices.kind === 'ok' ? notices.value.reduce((most, notice) => Math.max(most, notice.id), 0) : (this.unseen()?.id ?? 0);
    this.unseenSignal[1](null);
    await this.account.seeNotice(newest);
  }

  /** PF1, PF2: the fresh proof's first step with `password`: a grant, or the F step to take. */
  async prove(password: string): Promise<Outcome<Granted | SecondFactorNeeded>> {
    const who = this.who();
    const deps = this.account.flowDeps();
    if (who === null || deps === null) return FAILED;
    const outcome = await startTypedProof(deps, who.id, who.email, password, await this.account.highestFor(who.email));
    if (outcome.kind !== 'ok') return outcome;
    this.forget();
    this.provenFor = { account: who.id, typed: outcome.value.typed };
    return { kind: 'ok', value: outcome.value.answer };
  }

  /**
   * KG9, IN15: what the last proof's password gave, only while the account it proved is the one logged in (a logout or
   * another account's login meanwhile lets it go, zeroed).
   */
  private get proven(): Typed | null {
    if (this.provenFor !== null && this.provenFor.account !== this.who()?.id) this.forget();
    return this.provenFor?.typed ?? null;
  }

  /** KG9: the last proof's wrapping key zeroed and let go. */
  private forget(): void {
    this.provenFor?.typed.wrapping.fill(0);
    this.provenFor = null;
  }

  /** PF2: the proof's F step with `ticket`. */
  async proveSecond(ticket: string, proof: SecondProof): Promise<Outcome<Granted>> {
    const deps = this.account.flowDeps();
    return this.who() === null || deps === null ? FAILED : proveSecondStep(deps, ticket, proof);
  }

  /** `act` with the account logged in and the flows' dependencies; after it, the lists and view read again. */
  private async acting<T>(act: (deps: NonNullable<ReturnType<Account['flowDeps']>>, who: { readonly id: AccountId; readonly email: Email }) => Promise<Outcome<T>>): Promise<Outcome<T>> {
    const who = this.who();
    const deps = this.account.flowDeps();
    if (who === null || deps === null) return FAILED;
    const outcome = await act(deps, who);
    await this.load(true);
    return outcome;
  }

  /** The parameters a new password is derived under (C13): the highest seen for the address, else the default. */
  private async parametersFor(email: Email): Promise<KdfParameters> {
    return (await this.account.highestFor(email)) ?? KDF_DEFAULT;
  }

  /** CH1, C13: a new password with `grant`, derived at the highest parameters kept for the address. */
  changePassword(grant: string, password: string): Promise<Outcome<Advanced>> {
    const keyring = this.keyring;
    const rewrap = keyring === null ? null : (account: AccountId, next: Typed) => keyring.rewrap(account, next, this.proven);
    return this.acting(async (deps, who) => changePassword(deps, who.id, grant, password, await this.parametersFor(who.email), rewrap)).finally(() => {
      this.forget();
    });
  }

  /** JL3, CL: the waiting invite accepted with `grant` and the proof's password (failed with no proof or no keyring access). */
  async acceptInvite(grant: string): Promise<AcceptOutcome> {
    const typed = this.proven;
    try {
      return this.keyring === null || typed === null ? { kind: 'failed' } : await this.keyring.accept(grant, typed);
    } finally {
      this.forget();
    }
  }

  /** AS8 (KG11): the keyring opened with the proof's password (failed with no proof or no keyring access). */
  async openKeyring(): Promise<'opened' | 'none' | 'refused' | 'failed'> {
    const typed = this.proven;
    try {
      return this.keyring === null || typed === null ? 'failed' : await this.keyring.openKeyring(typed);
    } finally {
      this.forget();
    }
  }

  /** CH2: a new address `typed` with `grant`; one that isn't an address fails before any request. */
  changeEmail(grant: string, typed: string): Promise<Outcome<Advanced>> {
    const email = canonicalEmail(typed);
    return email === null ? Promise.resolve(FAILED) : this.acting((deps, who) => changeEmail(deps, who.id, grant, email));
  }

  /** CH3: revokes the device `device` with `grant`. */
  revoke(grant: string, device: string): Promise<Outcome<Advanced>> {
    return this.acting((deps, who) => revokeDevice(deps, who.id, grant, device));
  }

  /** CH4: a change's link opened on this device. */
  confirm(token: string): Promise<Outcome<Advanced>> {
    return this.acting((deps, who) => confirmLink(deps, who.id, token));
  }

  /** PD2, RQ4: cancels the change `change` with `grant`. */
  cancel(grant: string, change: number): Promise<Outcome<true>> {
    return this.acting((deps, who) => cancelChange(deps, who.id, grant, change));
  }

  /** FX2: starts turning F on with `password` typed: the link mailed, to open on this device. */
  startEnrolment(password: string, kind: FactorKind): Promise<Outcome<true>> {
    return this.acting(async (deps, who) => startEnrolment(deps, who.id, who.email, password, await this.account.highestFor(who.email), kind));
  }

  /** FX3: the enrolment's setup from its link. */
  async setUpEnrolment(token: string): Promise<Outcome<FactorSetup>> {
    const who = this.who();
    const deps = this.account.flowDeps();
    return who === null || deps === null ? FAILED : setUpEnrolment(deps, who.id, token);
  }

  /** FX4: the enrolment confirmed: its backup codes, shown once. */
  confirmEnrolment(token: string, proof: NewFactorProof): Promise<Outcome<readonly string[]>> {
    return this.acting((deps, who) => confirmEnrolment(deps, who.id, token, proof));
  }

  /** FX7: removes `factor` with `grant`. */
  removeFactor(grant: string, factor: NamedFactor): Promise<Outcome<Advanced>> {
    return this.acting((deps, who) => removeFactor(deps, who.id, grant, factor));
  }

  /** FX6: starts adding a factor of `kind` with `grant`: the change and its setup. */
  async addFactor(grant: string, kind: FactorKind): Promise<Outcome<{ readonly change: number; readonly setup: FactorSetup }>> {
    const who = this.who();
    const deps = this.account.flowDeps();
    return who === null || deps === null ? FAILED : addFactor(deps, who.id, grant, kind);
  }

  /** FX6: an add proved with the new factor. */
  confirmAdd(change: number, proof: NewFactorProof): Promise<Outcome<Advanced>> {
    return this.acting((deps, who) => confirmAdd(deps, who.id, change, proof));
  }

  /** FX8: new backup codes with `grant`, shown once. */
  newCodes(grant: string): Promise<Outcome<{ readonly outcome: Advanced; readonly codes: readonly string[] }>> {
    return this.acting((deps, who) => newBackupCodes(deps, who.id, grant));
  }

  /** DA1, FD1: asks for the account's deletion with `grant`; the date it is due is kept as seen here. */
  askDeletion(grant: string): Promise<Outcome<{ readonly dueAt: number }>> {
    return this.acting(async (deps, who) => {
      const outcome = await askDeletion(deps, who.id, grant);
      if (outcome.kind === 'ok') await this.account.deletionAsked(outcome.value.dueAt);
      return outcome;
    });
  }

  /** DA2, FD3: cancels the account's deletion with `grant`. */
  cancelDeletion(grant: string): Promise<Outcome<true>> {
    return this.acting(async (deps, who) => {
      const outcome = await cancelDeletion(deps, who.id, grant);
      if (outcome.kind === 'ok') await this.account.deletionCancelled();
      return outcome;
    });
  }

  /** TA2: accepts the terms and privacy policy of `versions` (the app's own). */
  async acceptTerms(versions: Versions): Promise<Outcome<TermsStatus>> {
    const reply = await this.account.post('/v1/account/terms', versions, readTermsAccepted);
    if (reply.kind !== 'ok') return problemOf(reply);
    this.termsSignal[1](reply.value);
    return { kind: 'ok', value: reply.value };
  }

  /** XP1: everything the relay holds about the account, as its answer, whole. */
  async exportData(): Promise<Outcome<Readonly<Record<string, unknown>>>> {
    const reply = await this.account.get('/v1/account/export', readExport);
    return reply.kind === 'ok' ? { kind: 'ok', value: reply.value } : problemOf(reply);
  }

  /** RQ1: asks a reset for `email` (logged in or not). */
  async requestReset(email: Email): Promise<Outcome<true>> {
    const deps = this.account.flowDeps();
    return deps === null ? FAILED : requestReset(deps, email);
  }

  /**
   * RQ2, C13: claims the reset whose link holds `token` with `password`, removing F when asked; the new parameters are
   * kept for the address once it applies (the next login keeps them, LG4).
   */
  async claimReset(token: string, password: string, removeFactor: boolean): Promise<Outcome<{ readonly account: AccountId; readonly dueAt: number }>> {
    const deps = this.account.flowDeps();
    if (deps === null) return FAILED;
    const outcome = await claimReset(deps, token, password, removeFactor, (await this.account.highestOfAll()) ?? KDF_DEFAULT);
    if (outcome.kind !== 'ok') return outcome;
    if (this.who() !== null) await this.load(true);
    return { kind: 'ok', value: { account: outcome.value.account, dueAt: outcome.value.dueAt } };
  }

  /** DA3: cancels the deletion whose mailed link holds `token` (no account needed). */
  async cancelDeletionByLink(token: string): Promise<Outcome<true>> {
    const deps = this.account.flowDeps();
    if (deps === null) return FAILED;
    const outcome = await cancelDeletionByLink(deps, token);
    if (outcome.kind === 'ok' && this.who() !== null) await this.load(true);
    return outcome;
  }
}
